import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaVideo, FaCopy, FaEnvelope, FaPhoneSlash, FaArrowLeft, FaFilePrescription } from 'react-icons/fa';
import { Button, Badge } from '../../Components/UI';
import JitsiRoom from '../../Components/VideoConsultation/JitsiRoom';
import {
  fetchSessionByAppointment,
  markHostJoined,
  endSession,
  sendInvite,
  buildPatientLink,
} from '../../Services/videoConsultation';
import { useGetLoggedInUser } from '../../UtilityHookFunctions/LoggedInUser';

const statusVariant = {
  Scheduled: 'default',
  Live: 'success',
  Ended: 'default',
  Cancelled: 'danger',
};

export default function VideoConsultation() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const user = useGetLoggedInUser();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inRoom, setInRoom] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchSessionByAppointment(appointmentId);
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load this consultation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  const patientLink = session?.guestToken ? buildPatientLink(session.guestToken) : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(patientLink);
      toast.success('Patient link copied');
    } catch (e) {
      // Clipboard API needs a secure context; fall back to a prompt.
      window.prompt('Copy the patient link:', patientLink);
    }
  };

  const emailLink = async () => {
    setInviting(true);
    try {
      const res = await sendInvite(session._id);
      toast.success(`Link emailed to ${res.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not send the invite');
    } finally {
      setInviting(false);
    }
  };

  const handleJoined = useCallback(async () => {
    try {
      await markHostJoined(session.hostToken, {
        role: user?.role || 'Doctor',
        name: user?.name,
        userId: user?.id || user?._id,
      });
      setSession((prev) => (prev ? { ...prev, status: 'Live' } : prev));
    } catch (e) {
      /* attendance logging is best-effort - never block the call */
    }
  }, [session?.hostToken, user]);

  const handleLeft = useCallback(() => setInRoom(false), []);

  const finish = async () => {
    setEnding(true);
    try {
      await endSession(session._id);
      setSession((prev) => (prev ? { ...prev, status: 'Ended' } : prev));
      setInRoom(false);
      toast.success('Consultation ended');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not end the consultation');
    } finally {
      setEnding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-lg rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
          <p className="font-medium text-rose-700">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" icon={<FaArrowLeft />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-800">
              <FaVideo className="text-violet-600" />
              Video Consultation
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              {session?.patientName} · {session?.bookDate}{' '}
              {session?.bookTimeSlot?.startTime
                ? `· ${session.bookTimeSlot.startTime} - ${session.bookTimeSlot.endTime || ''}`
                : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={statusVariant[session?.status] || 'default'}>{session?.status}</Badge>
          {session?.patientId && (
            <Button
              variant="outline"
              size="sm"
              icon={<FaFilePrescription />}
              onClick={() => navigate(`/prescription/add/${session.patientId}`)}
            >
              Prescription
            </Button>
          )}
        </div>
      </div>

      {/* Patient join link */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Patient join link
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
            {patientLink}
          </code>
          <Button variant="outline" size="sm" icon={<FaCopy />} onClick={copyLink}>
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<FaEnvelope />}
            isLoading={inviting}
            onClick={emailLink}
          >
            Email patient
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          The patient joins from this link without signing in. Share it only with them — anyone
          holding the link can enter the room.
        </p>
      </div>

      {/* Room */}
      {session?.status === 'Ended' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <p className="font-medium text-slate-700">This consultation has ended.</p>
          <p className="mt-1 text-sm text-slate-500">
            Started {session.startedAt ? new Date(session.startedAt).toLocaleString() : '—'} · ended{' '}
            {session.endedAt ? new Date(session.endedAt).toLocaleString() : '—'}
          </p>
        </div>
      ) : inRoom ? (
        <>
          <JitsiRoom
            domain={session.domain}
            roomName={session.roomName}
            displayName={user?.name || 'Doctor'}
            subject={`Consultation · ${session.patientName}`}
            isHost
            onJoined={handleJoined}
            onLeft={handleLeft}
            className="h-[70vh] w-full rounded-xl"
          />
          <div className="flex justify-end">
            <Button variant="danger" icon={<FaPhoneSlash />} isLoading={ending} onClick={finish}>
              End consultation
            </Button>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <FaVideo className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-medium text-slate-700">Ready when you are</p>
          <p className="mt-1 text-sm text-slate-500">
            The patient can enter from 15 minutes before the booked slot.
          </p>
          <Button className="mt-4" icon={<FaVideo />} onClick={() => setInRoom(true)}>
            Start consultation
          </Button>
        </div>
      )}
    </div>
  );
}
